import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
  Collapse,
  IconButton,
  CircularProgress,
  FormHelperText
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface Segment {
  id: string;
  name: string;
  memberCount: number;
}

interface Circle {
  id: string;
  name: string;
  memberCount: number;
}

export type TargetAudienceType = 'all' | 'segments' | 'circles' | '';

interface TargetAudienceSelectionProps {
  type: TargetAudienceType;
  selectedSegments: string[];
  selectedCircles: string[];
  segments: Segment[];
  circles: Circle[];
  onTypeChange: (type: TargetAudienceType) => void;
  onSegmentToggle: (segmentId: string) => void;
  onCircleToggle: (circleId: string) => void;
  isLoading?: boolean;
  error?: string;
  estimatedCustomers?: number;
  estimatedCircleCustomers?: number;
  disabled?: boolean;
}

const TargetAudienceSelection: React.FC<TargetAudienceSelectionProps> = ({
  type,
  selectedSegments,
  selectedCircles,
  segments,
  circles,
  onTypeChange,
  onSegmentToggle,
  onCircleToggle,
  isLoading = false,
  error,
  estimatedCustomers,
  estimatedCircleCustomers,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [segmentsExpanded, setSegmentsExpanded] = useState(true);
  const [circlesExpanded, setCirclesExpanded] = useState(true);

  const calculateSegmentTotal = () => {
    return selectedSegments.reduce((total, segmentId) => {
      const segment = segments.find(s => s.id === segmentId);
      return total + (segment?.memberCount || 0);
    }, 0);
  };

  const calculateCircleTotal = () => {
    return selectedCircles.reduce((total, circleId) => {
      const circle = circles.find(c => c.id === circleId);
      return total + (circle?.memberCount || 0);
    }, 0);
  };

  const renderEstimatedCustomers = () => {
    if (type === 'segments' && selectedSegments.length > 0) {
      const total = estimatedCustomers || calculateSegmentTotal();
      return (
        <Typography variant="body2" color="success.main" fontWeight="medium">
          {t('campaignExperimental.create.totalEstimatedCustomers')}: {total.toLocaleString()}
        </Typography>
      );
    }
    
    if (type === 'circles' && selectedCircles.length > 0) {
      const total = estimatedCircleCustomers || calculateCircleTotal();
      return (
        <Typography variant="body2" color="success.main" fontWeight="medium">
          {t('campaignExperimental.create.totalEstimatedCustomers')}: {total.toLocaleString()}
        </Typography>
      );
    }
    
    return null;
  };

  const renderSegmentsList = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    return (
      <FormGroup>
        <Box 
          sx={{ 
            maxHeight: segments.length > 5 ? '200px' : 'auto',
            overflowY: segments.length > 5 ? 'auto' : 'visible',
            pr: segments.length > 5 ? 1 : 0
          }}
        >
          {segments.map((segment) => (
            <FormControlLabel
              key={segment.id}
              control={
                <Checkbox
                  checked={selectedSegments.includes(segment.id)}
                  onChange={() => onSegmentToggle(segment.id)}
                  disabled={disabled}
                  sx={{ padding: '4px', ml: '8px' }}
                />
              }
              disabled={disabled}
              label={
                <Typography variant="body2" fontWeight="medium">
                  {segment.name} ({segment.memberCount.toLocaleString()} members)
                </Typography>
              }
              sx={{ 
                mb: 1,
                alignItems: 'center',
                '.MuiFormControlLabel-label': { ml: '4px' }
              }}
            />
          ))}
        </Box>
      </FormGroup>
    );
  };

  const renderCirclesList = () => {
    if (circles.length === 0) {
      return (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    return (
      <FormGroup>
        <Box 
          sx={{ 
            maxHeight: circles.length > 5 ? '200px' : 'auto',
            overflowY: circles.length > 5 ? 'auto' : 'visible',
            pr: circles.length > 5 ? 1 : 0
          }}
        >
          {circles.map((circle) => (
            <FormControlLabel
              key={circle.id}
              control={
                <Checkbox
                  checked={selectedCircles.includes(circle.id)}
                  onChange={() => onCircleToggle(circle.id)}
                  disabled={disabled}
                  sx={{ padding: '4px', ml: '8px' }}
                />
              }
              disabled={disabled}
              label={
                <Typography variant="body2" fontWeight="medium">
                  {circle.name}
                </Typography>
              }
              sx={{ 
                mb: 1,
                alignItems: 'center',
                '.MuiFormControlLabel-label': { ml: '4px' }
              }}
            />
          ))}
        </Box>
      </FormGroup>
    );
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="h6" fontWeight="medium">
            {t('campaignExperimental.create.targetAudienceTitle')}
          </Typography>
          {renderEstimatedCustomers()}
        </Box>
        <Divider sx={{ mb: 3 }} />
        
        <FormControl component="fieldset" fullWidth error={!!error} disabled={disabled}>
          <RadioGroup
            value={type}
            onChange={(e) => onTypeChange(e.target.value as TargetAudienceType)}
          >
            {/* All Members */}
            <FormControlLabel
              value="all"
              control={<Radio />}
              disabled={disabled}
              label={
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {t('campaigns.allMembers')}
                  </Typography>
                </Box>
              }
              sx={{ mb: 1, alignItems: 'center' }}
            />

            {/* Specific Segments */}
            <FormControlLabel
              value="segments"
              control={<Radio />}
              disabled={disabled}
              label={
                <Box sx={{ width: '100%' }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      cursor: type === 'segments' ? 'pointer' : 'default',
                    }}
                    onClick={() => {
                      if (type === 'segments') {
                        setSegmentsExpanded(!segmentsExpanded);
                      }
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {t('campaignExperimental.create.memberSegmentsTitle')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('campaignExperimental.create.memberSegmentsDesc')}
                      </Typography>
                    </Box>
                    {type === 'segments' && (
                      <IconButton 
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSegmentsExpanded(!segmentsExpanded);
                        }}
                      >
                        {segmentsExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    )}
                  </Box>
                  
                  <Collapse in={type === 'segments' && segmentsExpanded}>
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {renderSegmentsList()}
                    </Box>
                  </Collapse>
                </Box>
              }
              sx={{ mb: 1, alignItems: 'flex-start', width: '100%' }}
            />

            {/* Status Circles */}
            <FormControlLabel
              value="circles"
              control={<Radio />}
              disabled={disabled}
              label={
                <Box sx={{ width: '100%' }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      cursor: type === 'circles' ? 'pointer' : 'default'
                    }}
                    onClick={() => {
                      if (type === 'circles') {
                        setCirclesExpanded(!circlesExpanded);
                      }
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {t('campaignExperimental.create.statusCirclesTitle')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('campaignExperimental.create.statusCirclesDesc')}
                      </Typography>
                    </Box>
                    {type === 'circles' && (
                      <IconButton 
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCirclesExpanded(!circlesExpanded);
                        }}
                      >
                        {circlesExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    )}
                  </Box>
                  
                  <Collapse in={type === 'circles' && circlesExpanded}>
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {renderCirclesList()}
                    </Box>
                  </Collapse>
                </Box>
              }
              sx={{ alignItems: 'flex-start', width: '100%' }}
            />
          </RadioGroup>
          {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      </CardContent>
    </Card>
  );
};

export default TargetAudienceSelection;